(() => {
  "use strict";

  const SELECTORS = {
    header: "[data-minaco-header]",
    surface: "[data-header-surface]",
    main: "[data-header-main], .minaco-header__main",
    navigationZone:
      '[data-header-zone="navigation"], [data-header-hotspot="nav"], [data-desktop-navigation]',
    logoZone: '[data-header-zone="logo"], [data-header-hotspot="logo"]',
    actionsZone:
      '[data-header-zone="actions"], [data-header-hotspot="actions"]',
    normalNavItem: ".minaco-header__nav-item:not([data-mega-trigger])",
    megaTrigger: "[data-mega-trigger]",
    megaMenu: "[data-mega-menu]",
    megaInner: ".minaco-header__mega-inner",
    megaImage: ".minaco-header__mega-image",
    searchTrigger: "[data-search-open]",
    searchPanel: "[data-search-panel]",
    searchClose: "[data-search-close]",
    searchForm: "[data-search-form]",
    searchInput: "[data-search-input]",
    searchResults: "[data-search-results]",
    countrySelector: "[data-country-selector]",
    countryToggle: "[data-country-toggle]",
    countryDropdown: "[data-country-dropdown]",
    countryInput: "[data-country-input]",
    countryOption: "[data-country-code]",
    mobileOpen: "[data-mobile-menu-open]",
    mobileDrawer: "[data-mobile-drawer]",
    mobilePanel: ".minaco-mobile-drawer__panel",
    mobileClose: "[data-mobile-menu-close]",
    mobileSubmenuToggle: "[data-mobile-submenu-toggle]",
    mobileSubmenu: "[data-mobile-submenu]",
    focusable:
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  };

  const DESKTOP_MEDIA = "(min-width: 990px)";
  const HOVER_MEDIA = "(hover: hover) and (pointer: fine)";
  const REDUCED_MOTION_MEDIA = "(prefers-reduced-motion: reduce)";

  class MinacoHeader {
    constructor(header) {
      this.header = header;
      this.events = new AbortController();
      this.searchRequest = null;
      this.searchCache = new Map();
      this.timers = new Map();
      this.resizeFrame = 0;
      this.scrollFrame = 0;
      this.resizeObserver = null;

      this.surface = header.querySelector(SELECTORS.surface);
      this.main = header.querySelector(SELECTORS.main);
      this.navigationZone = header.querySelector(SELECTORS.navigationZone);
      this.logoZone = header.querySelector(SELECTORS.logoZone);
      this.actionsZone = header.querySelector(SELECTORS.actionsZone);

      this.megaTriggers = [...header.querySelectorAll(SELECTORS.megaTrigger)];
      this.megaMenus = [...header.querySelectorAll(SELECTORS.megaMenu)];
      this.activeMegaMenu = null;
      this.activeMegaTrigger = null;

      this.searchTriggers = [
        ...header.querySelectorAll(SELECTORS.searchTrigger),
      ];

      this.searchPanel = header.querySelector(SELECTORS.searchPanel);

      this.searchCloseButtons = [
        ...header.querySelectorAll(SELECTORS.searchClose),
      ];

      this.searchForm = header.querySelector(SELECTORS.searchForm);
      this.searchInput = header.querySelector(SELECTORS.searchInput);
      this.searchResults = header.querySelector(SELECTORS.searchResults);
      this.searchProductTitleFont =
        this.searchPanel?.dataset.productTitleFont || "theme-default";
      this.searchProductPriceFont =
        this.searchPanel?.dataset.productPriceFont || "theme-default";

      this.countrySelector = header.querySelector(SELECTORS.countrySelector);
      this.countryToggle = header.querySelector(SELECTORS.countryToggle);
      this.countryDropdown = header.querySelector(SELECTORS.countryDropdown);
      this.countryInput = header.querySelector(SELECTORS.countryInput);

      this.mobileOpenButton = header.querySelector(SELECTORS.mobileOpen);
      this.mobileDrawer = header.querySelector(SELECTORS.mobileDrawer);
      this.mobilePanel = header.querySelector(SELECTORS.mobilePanel);

      this.mobileCloseButtons = [
        ...header.querySelectorAll(SELECTORS.mobileClose),
      ];

      this.mobileSubmenuToggles = [
        ...header.querySelectorAll(SELECTORS.mobileSubmenuToggle),
      ];

      this.desktopMedia = window.matchMedia(DESKTOP_MEDIA);
      this.hoverMedia = window.matchMedia(HOVER_MEDIA);
      this.reducedMotionMedia = window.matchMedia(REDUCED_MOTION_MEDIA);

      this.scrollThreshold = this.numberFromData("scrollThreshold", 8);

      this.searchMinimumCharacters = this.numberFromData(
        "searchMinimumCharacters",
        2,
      );

      this.searchLimit = this.numberFromData("searchLimit", 8);
      this.megaCloseDelay = this.numberFromData("megaCloseDelay", 170);
      this.transparentDelay = this.numberFromData("transparentDelay", 90);

      this.scrolled = false;
      this.pointerSolid = false;
      this.searchOpen = false;
      this.countryOpen = false;
      this.mobileOpen = false;
      this.scrollLocked = false;
      this.lockedDocumentStyles = null;

      this.searchReturnFocus = null;
      this.countryReturnFocus = null;
      this.mobileReturnFocus = null;

      this.canBeTransparent = this.resolveTransparentState();

      this.init();
    }

    init() {
      this.prepareAccessibility();
      this.bindHeaderZones();
      this.bindMegaMenus();
      this.bindSearch();
      this.bindCountrySelector();
      this.bindMobileDrawer();
      this.bindMobileSubmenus();
      this.bindGlobalEvents();
      this.bindMeasurements();
      this.updateScrollState();
      this.resetMegaHeight();
      this.reconcileState();

      window.requestAnimationFrame(() => {
        if (!this.events.signal.aborted) {
          this.header.classList.add("is-ready");
        }
      });
    }

    prepareAccessibility() {
      this.searchResults?.setAttribute("aria-live", "polite");
      this.searchResults?.setAttribute("aria-busy", "false");
      this.searchPanel?.setAttribute("aria-hidden", "true");
      this.countryDropdown?.setAttribute("aria-hidden", "true");
      this.mobileDrawer?.setAttribute("aria-hidden", "true");

      this.megaMenus.forEach((menu) => {
        menu.setAttribute("aria-hidden", "true");
      });

      this.mobileSubmenuToggles.forEach((toggle, index) => {
        const submenu = this.getMobileSubmenu(toggle);

        if (!submenu) {
          return;
        }

        if (!submenu.id) {
          submenu.id = `${
            this.header.id || "MinacoHeader"
          }-MobileSubmenu-${index + 1}`;
        }

        toggle.setAttribute("aria-controls", submenu.id);
        toggle.setAttribute("aria-expanded", "false");
        submenu.setAttribute("aria-hidden", "true");
      });
    }

    setHeightVariables(headerHeight, submenuHeight, fullHeight) {
      const targets = [this.surface, this.header].filter(Boolean);

      const variables = {
        "--header-height-current": `${headerHeight}px`,

        "--submenu-height": `${submenuHeight}px`,

        "--full-open-header-height": `${fullHeight}px`,

        "--minaco-header-current-height": `${headerHeight}px`,

        "--minaco-submenu-height": `${submenuHeight}px`,

        "--minaco-mega-height": `${submenuHeight}px`,

        "--minaco-mega-content-height": `${submenuHeight}px`,

        "--minaco-open-height": `${fullHeight}px`,

        "--minaco-header-open-height": `${fullHeight}px`,

        "--minaco-header-expanded-height": `${fullHeight}px`,

        "--minaco-surface-open-height": `${fullHeight}px`,

        "--minaco-full-height": `${fullHeight}px`,

        "--minaco-full-open-header-height": `${fullHeight}px`,
      };

      targets.forEach((target) => {
        Object.entries(variables).forEach(([name, value]) => {
          target.style.setProperty(name, value);
        });
      });
    }

    bindGlobalEvents() {
      this.on(
        window,
        "scroll",
        () => {
          if (this.scrollFrame) {
            return;
          }

          this.scrollFrame = window.requestAnimationFrame(() => {
            this.scrollFrame = 0;
            this.updateScrollState();
          });
        },
        {
          passive: true,
        },
      );

      this.on(document, "pointerdown", (event) => {
        const target = event.target;

        if (!(target instanceof Node)) {
          return;
        }

        if (this.countryOpen && !this.countrySelector?.contains(target)) {
          this.closeCountrySelector();
        }

        if (
          this.activeMegaMenu &&
          !this.navigationZone?.contains(target) &&
          !this.activeMegaMenu.contains(target)
        ) {
          this.closeMegaMenus();
        }
      });

      this.on(document, "focusin", (event) => {
        const target = event.target;

        if (!(target instanceof Node)) {
          return;
        }

        if (this.countryOpen && !this.countrySelector?.contains(target)) {
          this.closeCountrySelector();
        }

        if (
          this.activeMegaMenu &&
          !this.navigationZone?.contains(target) &&
          !this.activeMegaMenu.contains(target)
        ) {
          this.closeMegaMenus();
        }
      });

      this.on(document, "keydown", (event) => {
        this.handleDocumentKeydown(event);
      });

      this.on(window, "pageshow", () => {
        this.updateScrollState();
      });
    }

    bindMeasurements() {
      this.header.querySelectorAll(SELECTORS.megaImage).forEach((image) => {
        if (image.complete) {
          return;
        }

        this.on(
          image,
          "load",
          () => {
            if (this.activeMegaMenu?.contains(image)) {
              this.updateMegaHeight(this.activeMegaMenu);
            }
          },
          {
            once: true,
          },
        );
      });

      if ("ResizeObserver" in window) {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.resizeFrame) {
            return;
          }

          this.resizeFrame = window.requestAnimationFrame(() => {
            this.resizeFrame = 0;

            if (this.activeMegaMenu) {
              this.updateMegaHeight(this.activeMegaMenu);
            } else {
              this.resetMegaHeight();
            }
          });
        });

        if (this.main) {
          this.resizeObserver.observe(this.main);
        }

        this.megaMenus.forEach((menu) => {
          const inner = menu.querySelector(SELECTORS.megaInner);

          if (inner) {
            this.resizeObserver.observe(inner);
          }
        });
      }

      this.on(
        window,
        "resize",
        () => {
          this.handleResize();
        },
        {
          passive: true,
        },
      );
    }

    handleResize() {
      if (this.resizeFrame) {
        return;
      }

      this.resizeFrame = window.requestAnimationFrame(() => {
        this.resizeFrame = 0;

        if (this.desktopMedia.matches) {
          this.closeMobileDrawer({
            reconcile: false,
          });
        } else {
          this.closeMegaMenus({
            reconcile: false,
            immediateReset: true,
          });

          this.closeCountrySelector({
            reconcile: false,
          });
        }

        if (this.activeMegaMenu) {
          this.updateMegaHeight(this.activeMegaMenu);
        } else {
          this.resetMegaHeight();
        }

        this.reconcileState();
      });
    }

    updateScrollState() {
      this.scrolled = window.scrollY > this.scrollThreshold;

      this.reconcileState();
    }

    reconcileState() {
      const megaOpen = Boolean(this.activeMegaMenu);

      const overlayOpen =
        megaOpen || this.searchOpen || this.countryOpen || this.mobileOpen;

      const solid =
        !this.canBeTransparent ||
        this.scrolled ||
        this.pointerSolid ||
        overlayOpen;

      this.header.classList.toggle("is-scrolled", this.scrolled);

      this.header.classList.toggle("is-solid", solid);

      this.header.classList.toggle("is-transparent", !solid);

      this.header.classList.toggle("is-mega-open", megaOpen);

      this.header.classList.toggle("is-search-open", this.searchOpen);

      this.header.classList.toggle("is-country-open", this.countryOpen);

      this.header.classList.toggle("is-mobile-open", this.mobileOpen);

      this.header.dataset.headerState = this.mobileOpen
        ? "mobile"
        : this.searchOpen
          ? "search"
          : this.countryOpen
            ? "country"
            : megaOpen
              ? "mega"
              : solid
                ? "solid"
                : "transparent";
    }

    scheduleTransparentRestore() {
      this.setTimer(
        "transparent",
        () => {
          this.pointerSolid = this.isPointerInsideHeaderZone();

          if (!this.pointerSolid && !this.isPointerInsideMenuZone()) {
            this.closeMegaMenus({
              reconcile: false,
            });
          }

          this.reconcileState();
        },
        this.transparentDelay,
      );
    }

    handleDocumentKeydown(event) {
      if (event.key === "Tab") {
        const modal = this.searchOpen
          ? this.searchPanel
          : this.mobileOpen
            ? this.mobileDrawer
            : null;

        if (modal) {
          this.trapFocus(event, modal);
        }

        return;
      }

      if (event.key !== "Escape") {
        return;
      }

      if (this.searchOpen) {
        event.preventDefault();

        this.closeSearch({
          restoreFocus: true,
        });
      } else if (this.mobileOpen) {
        event.preventDefault();

        this.closeMobileDrawer({
          restoreFocus: true,
        });
      } else if (this.countryOpen) {
        event.preventDefault();

        this.closeCountrySelector({
          restoreFocus: true,
        });
      } else if (this.activeMegaMenu) {
        event.preventDefault();

        this.closeMegaMenus({
          restoreFocus: true,
        });
      }
    }

    trapFocus(event, container) {
      const focusable = [
        ...container.querySelectorAll(SELECTORS.focusable),
      ].filter((element) => this.isVisible(element));

      if (!focusable.length) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!container.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    syncDocumentScrollLock() {
      const shouldLock = this.searchOpen || this.mobileOpen;

      if (shouldLock === this.scrollLocked) {
        return;
      }

      if (shouldLock) {
        const root = document.documentElement;

        const scrollbarWidth = Math.max(
          0,
          window.innerWidth - root.clientWidth,
        );

        this.lockedDocumentStyles = {
          overflow: root.style.overflow,

          paddingRight: root.style.paddingRight,
        };

        root.style.overflow = "hidden";

        if (scrollbarWidth) {
          root.style.paddingRight = `${scrollbarWidth}px`;
        }

        root.classList.add("minaco-header-scroll-locked");

        this.scrollLocked = true;

        return;
      }

      const root = document.documentElement;

      root.style.overflow = this.lockedDocumentStyles?.overflow || "";

      root.style.paddingRight = this.lockedDocumentStyles?.paddingRight || "";

      root.classList.remove("minaco-header-scroll-locked");

      this.lockedDocumentStyles = null;
      this.scrollLocked = false;
    }

    resolveTransparentState() {
      if (this.header.dataset.forceSolid === "true") {
        return false;
      }

      const setting = this.header.dataset.transparentHome;

      const enabled = setting === undefined ? true : setting === "true";

      const rootPath = this.normalizePath(window.Shopify?.routes?.root || "/");

      const currentPath = this.normalizePath(window.location.pathname);

      const transparentSection = document.querySelector(
        "[data-minaco-transparent-header-page]",
      );

      return (enabled && currentPath === rootPath) || Boolean(transparentSection);
    }

    normalizePath(path) {
      const normalized = String(path || "/").replace(/\/+$/, "");

      return normalized || "/";
    }

    relatedTargetIsMenuZone(target) {
      if (!(target instanceof Node)) {
        return false;
      }

      return Boolean(
        this.navigationZone?.contains(target) ||
        this.activeMegaMenu?.contains(target),
      );
    }

    isPointerInsideMenuZone() {
      if (!this.hoverMedia.matches) {
        return false;
      }

      return Boolean(
        this.navigationZone?.matches(":hover") ||
        this.activeMegaMenu?.matches(":hover"),
      );
    }

    isPointerInsideHeaderZone() {
      if (!this.hoverMedia.matches) {
        return false;
      }

      return [this.navigationZone, this.logoZone, this.actionsZone].some(
        (zone) => zone?.matches(":hover"),
      );
    }

    isFinePointer(event) {
      return this.hoverMedia.matches && event.pointerType !== "touch";
    }

    firstFocusable(container) {
      return [...(container?.querySelectorAll(SELECTORS.focusable) || [])].find(
        (element) => this.isVisible(element),
      );
    }

    isVisible(element) {
      return (
        Boolean(element?.getClientRects().length) &&
        getComputedStyle(element).visibility !== "hidden"
      );
    }

    isConnected(element) {
      return element instanceof HTMLElement && element.isConnected;
    }

    label(name, fallback) {
      const value = this.searchPanel?.dataset?.[name];

      return value || fallback;
    }

    numberFromData(name, fallback) {
      const rawValue = this.header.dataset[name];

      if (rawValue === undefined || rawValue === "") {
        return fallback;
      }

      const value = Number(rawValue);

      return Number.isFinite(value) ? value : fallback;
    }

    on(target, eventName, handler, options = {}) {
      if (!target?.addEventListener) {
        return;
      }

      target.addEventListener(eventName, handler, {
        ...options,
        signal: this.events.signal,
      });
    }

    setTimer(name, callback, delay) {
      this.clearTimer(name);

      const timer = window.setTimeout(() => {
        this.timers.delete(name);
        callback();
      }, delay);

      this.timers.set(name, timer);
    }

    clearTimer(name) {
      const timer = this.timers.get(name);

      if (!timer) {
        return;
      }

      window.clearTimeout(timer);
      this.timers.delete(name);
    }

    destroy() {
      this.events.abort();
      this.abortSearchRequest();

      this.timers.forEach((timer) => {
        window.clearTimeout(timer);
      });

      this.timers.clear();

      this.resizeObserver?.disconnect();

      if (this.resizeFrame) {
        window.cancelAnimationFrame(this.resizeFrame);
      }

      if (this.scrollFrame) {
        window.cancelAnimationFrame(this.scrollFrame);
      }

      this.searchOpen = false;
      this.mobileOpen = false;

      this.syncDocumentScrollLock();

      this.header.classList.remove("is-ready");

      delete this.header.dataset.minacoInitialized;

      delete this.header.__minacoHeader;
    }
  }

  const featureInstallers = window.MinacoHeaderFeatures || {};

  ["mega", "search", "country", "mobile"].forEach((featureName) => {
    const installFeature = featureInstallers[featureName];

    if (typeof installFeature !== "function") {
      throw new Error(`[Mina&Co Header] Missing feature module: ${featureName}`);
    }

    installFeature(MinacoHeader, SELECTORS);
  });

  function collectHeaders(root = document) {
    const headers = [];

    if (root instanceof Element && root.matches(SELECTORS.header)) {
      headers.push(root);
    }

    root.querySelectorAll?.(SELECTORS.header).forEach((header) => {
      headers.push(header);
    });

    return headers;
  }

  function initMinacoHeaders(root = document) {
    collectHeaders(root).forEach((header) => {
      if (header.dataset.minacoInitialized === "true") {
        return;
      }

      try {
        header.__minacoHeader = new MinacoHeader(header);

        header.dataset.minacoInitialized = "true";
      } catch (error) {
        delete header.dataset.minacoInitialized;

        console.error("[Mina&Co Header] Initialization failed.", error);
      }
    });
  }

  function destroyMinacoHeaders(root) {
    collectHeaders(root).forEach((header) => {
      header.__minacoHeader?.destroy();
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener(
      "DOMContentLoaded",
      () => {
        initMinacoHeaders();
      },
      {
        once: true,
      },
    );
  } else {
    initMinacoHeaders();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initMinacoHeaders(event.target);
  });

  document.addEventListener("shopify:section:unload", (event) => {
    destroyMinacoHeaders(event.target);
  });
})();
